<script lang="ts">
  /**
   * Toolbar popover for showing / collapsing project lanes.
   * Shares state with the lane header toggles via the parent.
   */
  import { canCollapseLane, PROJECT_LANES, type ProjectLane } from '../lib/projectLanesUtils';

  interface Props {
    collapsedLanes: ReadonlySet<ProjectLane>;
    onToggleLane: (lane: ProjectLane) => void;
    onShowAll: () => void;
  }

  let { collapsedLanes, onToggleLane, onShowAll }: Props = $props();

  let isOpen = $state(false);
  let popoverEl: HTMLDivElement | undefined = $state();

  let collapsedSet = $derived(
    collapsedLanes instanceof Set ? collapsedLanes : new Set<ProjectLane>(collapsedLanes ?? []),
  );
  let isCustomized = $derived(collapsedSet.size > 0);

  function toggle(e: MouseEvent) {
    e.stopPropagation();
    isOpen = !isOpen;
  }

  function close() {
    isOpen = false;
  }

  function handleWindowClick(e: MouseEvent) {
    if (isOpen && popoverEl && !popoverEl.contains(e.target as Node)) {
      close();
    }
  }
</script>

<svelte:window onclick={handleWindowClick} />

<div class="columns-options" bind:this={popoverEl}>
  <button
    type="button"
    class="columns-trigger"
    class:active={isOpen || isCustomized}
    aria-expanded={isOpen}
    aria-haspopup="true"
    onclick={toggle}
  >
    Columns
    <span class="columns-chevron" aria-hidden="true">{isOpen ? '▲' : '▼'}</span>
  </button>

  {#if isOpen}
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
    <div class="popover" onclick={(e) => e.stopPropagation()}>
      <div class="popover-header">
        <span class="popover-title">Visible columns</span>
      </div>
      <div class="option-list">
        {#each PROJECT_LANES as lane (lane.key)}
          {@const isCollapsed = collapsedSet.has(lane.key)}
          {@const canCollapse = canCollapseLane(collapsedSet, lane.key)}
          <label class="option" class:disabled={!isCollapsed && !canCollapse}>
            <input
              type="checkbox"
              checked={!isCollapsed}
              disabled={!isCollapsed && !canCollapse}
              onchange={() => onToggleLane(lane.key)}
            />
            <span class="option-icon">{lane.icon}</span>
            <span class="option-label">{lane.label}</span>
          </label>
        {/each}
      </div>
      {#if isCustomized}
        <div class="popover-footer">
          <button type="button" class="show-all-btn" onclick={onShowAll}>Show all</button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .columns-options {
    position: relative;
  }

  .columns-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .columns-trigger:hover,
  .columns-trigger.active {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #2563eb;
  }

  .columns-chevron {
    font-size: 0.5rem;
    color: #9ca3af;
  }

  .popover {
    position: absolute;
    top: calc(100% + 0.25rem);
    right: 0;
    width: 11.5rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 70;
    overflow: hidden;
  }

  .popover-header {
    padding: 0.375rem 0.625rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .popover-title {
    font-size: 0.625rem;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .option-list {
    padding: 0.25rem 0;
  }

  .option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    cursor: pointer;
  }

  .option:hover {
    background: #f3f4f6;
  }

  .option.disabled {
    cursor: default;
    opacity: 0.55;
  }

  .option input[type='checkbox'] {
    width: 0.8125rem;
    height: 0.8125rem;
    accent-color: #3b82f6;
    margin: 0;
    cursor: pointer;
    flex-shrink: 0;
  }

  .option.disabled input[type='checkbox'] {
    cursor: default;
  }

  .option-icon {
    font-size: 0.75rem;
    line-height: 1;
  }

  .option-label {
    color: #374151;
  }

  .popover-footer {
    padding: 0.25rem 0;
    border-top: 1px solid #f3f4f6;
  }

  .show-all-btn {
    display: block;
    width: 100%;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    color: #6b7280;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
  }

  .show-all-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }
</style>
